import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  useMediaQuery,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Checkbox,
} from '@mui/material';

/**
 * ResponsiveTable - A table component that switches to card view on mobile
 * 
 * Props:
 * - columns: Array of { field, headerName, width?, renderCell?, align?, mobileHidden?, mobilePrimary?, mobileSecondary? }
 * - rows: Array of data objects
 * - onRowClick: Optional function called when row/card is clicked
 * - getRowId: Function to get unique id from row (default: row => row.id)
 * - selectable: Boolean to enable row selection
 * - selectedIds: Array of selected row ids
 * - onSelectionChange: Function called with array of selected ids
 * - actions: Function(row) returning action buttons for each row
 * - emptyMessage: Message to show when no data
 * - stickyHeader: Boolean for sticky table header on desktop
 */
export default function ResponsiveTable({
  columns,
  rows,
  onRowClick,
  getRowId = (row) => row.id,
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  actions,
  emptyMessage = 'No data available',
  stickyHeader = false,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      onSelectionChange?.(rows.map(row => getRowId(row)));
    } else {
      onSelectionChange?.([]);
    }
  };

  const handleSelectRow = (rowId) => {
    const currentIndex = selectedIds.indexOf(rowId);
    const newSelected = [...selectedIds];

    if (currentIndex === -1) {
      newSelected.push(rowId);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    onSelectionChange?.(newSelected);
  };

  const isSelected = (rowId) => selectedIds.indexOf(rowId) !== -1;

  // Get primary field for mobile card title
  const primaryColumn = columns.find(col => col.mobilePrimary) || columns[0];
  // Get secondary fields for mobile card subtitle
  const secondaryColumns = columns.filter(col => col.mobileSecondary);
  // Get visible columns for mobile (exclude hidden ones)
  const mobileColumns = columns.filter(col => !col.mobileHidden && col !== primaryColumn);

  // Mobile Card View
  if (isMobile) {
    if (rows.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">{emptyMessage}</Typography>
        </Box>
      );
    }

    return (
      <Stack spacing={1.5}>
        {rows.map((row) => {
          const rowId = getRowId(row);
          const selected = isSelected(rowId);
          
          return (
            <Card 
              key={rowId}
              variant="outlined"
              sx={{ 
                cursor: onRowClick ? 'pointer' : 'default',
                bgcolor: selected ? 'action.selected' : 'background.paper',
                '&:hover': onRowClick ? { bgcolor: 'action.hover' } : {},
                transition: 'background-color 0.2s',
              }}
              onClick={() => onRowClick?.(row)}
            >
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                {/* Header row with checkbox, title, and actions */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                  {selectable && (
                    <Checkbox
                      checked={selected}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleSelectRow(rowId);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      size="small"
                      sx={{ mr: 1, mt: -0.5, ml: -1 }}
                    />
                  )}
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight="medium" noWrap>
                      {primaryColumn.renderCell 
                        ? primaryColumn.renderCell({ row, value: row[primaryColumn.field] })
                        : row[primaryColumn.field]
                      }
                    </Typography>
                    {secondaryColumns.length > 0 && (
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {secondaryColumns.map((col, idx) => (
                          <span key={col.field}>
                            {idx > 0 && ' • '}
                            {col.renderCell 
                              ? col.renderCell({ row, value: row[col.field] })
                              : row[col.field]
                            }
                          </span>
                        ))}
                      </Typography>
                    )}
                  </Box>
                  {actions && (
                    <Box sx={{ ml: 1, display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                      {actions(row)}
                    </Box>
                  )}
                </Box>

                {/* Detail fields */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {mobileColumns
                    .filter(col => !col.mobileSecondary && col.field !== primaryColumn.field)
                    .map((col) => {
                      const value = col.renderCell 
                        ? col.renderCell({ row, value: row[col.field] })
                        : row[col.field];
                      
                      // Skip empty values
                      if (value === null || value === undefined || value === '' || value === 'N/A') {
                        return null;
                      }

                      return (
                        <Box 
                          key={col.field} 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                            px: 1,
                            py: 0.5,
                            minWidth: 'fit-content',
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                            {col.headerName}:
                          </Typography>
                          <Typography variant="caption" fontWeight="medium">
                            {value}
                          </Typography>
                        </Box>
                      );
                    })}
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
    );
  }

  // Desktop Table View
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small" stickyHeader={stickyHeader}>
        <TableHead>
          {/* Header styling (surface-2 bg, uppercase-xs labels) comes from the theme's MuiTableCell overrides */}
          <TableRow>
            {selectable && (
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedIds.length > 0 && selectedIds.length < rows.length}
                  checked={rows.length > 0 && selectedIds.length === rows.length}
                  onChange={handleSelectAll}
                  size="small"
                />
              </TableCell>
            )}
            {columns.map((col) => (
              <TableCell
                key={col.field}
                align={col.align || 'left'}
                sx={{ width: col.width }}
              >
                {col.headerName}
              </TableCell>
            ))}
            {actions && <TableCell align="center">Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell 
                colSpan={columns.length + (selectable ? 1 : 0) + (actions ? 1 : 0)} 
                align="center"
                sx={{ py: 4 }}
              >
                <Typography color="text.secondary">{emptyMessage}</Typography>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => {
              const rowId = getRowId(row);
              const selected = isSelected(rowId);

              return (
                <TableRow 
                  key={rowId}
                  hover
                  selected={selected}
                  onClick={() => onRowClick?.(row)}
                  sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                >
                  {selectable && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected}
                        onChange={() => handleSelectRow(rowId)}
                        onClick={(e) => e.stopPropagation()}
                        size="small"
                      />
                    </TableCell>
                  )}
                  {columns.map((col) => (
                    <TableCell 
                      key={col.field}
                      align={col.align || 'left'}
                      sx={{ 
                        maxWidth: col.width,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {col.renderCell 
                        ? col.renderCell({ row, value: row[col.field] })
                        : row[col.field]
                      }
                    </TableCell>
                  ))}
                  {actions && (
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                        {actions(row)}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/**
 * MobileOnlyCard - Wrapper that only shows content on mobile
 */
export function MobileOnlyCard({ children }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  if (!isMobile) return null;
  return children;
}

/**
 * DesktopOnly - Wrapper that only shows content on desktop
 */
export function DesktopOnly({ children }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  if (isMobile) return null;
  return children;
}

/**
 * useIsMobile - Hook to check if on mobile
 */
export function useIsMobile() {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down('sm'));
}
